import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { Shell } from '../components/Shell'
import { Sill, Spinner } from '../components/ui'
import Markdown from '../components/Markdown'

/**
 * Privacy Policy and Terms, read from policy_documents.
 *
 * The text lives in the database rather than in this bundle because it is
 * versioned and dated there, and because user_consents points at a specific
 * version — a policy pasted into JSX could be edited without anything
 * recording that it changed, which is the one thing a consent record exists to
 * prevent. It also means correcting a typo in the Terms does not require a
 * rebuild and a redeploy.
 *
 * Shows the newest row whose effective_from has passed, so a replacement can
 * be written and dated ahead of time without going live early.
 */

const KINDS = {
  privacy: { eyebrow: 'Legal', title: 'Privacy' },
  terms: { eyebrow: 'Legal', title: 'Terms' },
}

export default function Policy() {
  const { kind = 'privacy' } = useParams()
  const meta = KINDS[kind] ?? KINDS.privacy

  const [doc, setDoc] = useState(undefined) // undefined = loading, null = none
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('policy_documents')
      .select('kind, version, effective_from, summary, body_md')
      .eq('kind', kind)
      .lte('effective_from', new Date().toISOString())
      .order('effective_from', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) { setErr(readableError(error)); setDoc(null); return }
    setDoc(data ?? null)
  }, [kind])

  useEffect(() => { load() }, [load])

  if (doc === undefined) return <Shell tabs back="/account" title="Account"><Spinner /></Shell>

  return (
    <Shell tabs back="/account" title="Account">
      <div className="eyebrow">{meta.eyebrow}</div>
      <h2 className="q">{meta.title}</h2>

      {doc ? (
        <>
          <p className="sub">
            Version {doc.version} · in force since{' '}
            {new Date(doc.effective_from).toLocaleDateString()}
          </p>
          <Sill />
          {doc.summary && <p style={{ marginBottom: 16 }}>{doc.summary}</p>}
          <Markdown source={doc.body_md} />
        </>
      ) : (
        <>
          <p className="sub">This one has not been published yet.</p>
          <Sill />
          <p className="small">
            Nothing is being hidden — the document genuinely has not been
            written. Until it is, nothing here claims to tell you how your
            words are handled, and you should read that absence as the answer
            rather than assume there is a policy somewhere you have not seen.
          </p>
        </>
      )}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
